import { useCallback, useMemo } from "react";
import { useHistory, useLocation } from "react-router-dom";

function useQueryParams(defaultParams = {}) {
  const history = useHistory();
  const location = useLocation();

  // Get current params as object
  const params = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const paramsObj = {};
    for (let [key, value] of searchParams) {
      paramsObj[key] = value;
    }
    return paramsObj;
  }, [location.search]);

  // 1️⃣ Add/Update params (KEEPS existing params)
  const addOrUpdateParams = useCallback(
    (newParams) => {
      const searchParams = new URLSearchParams(location.search);

      Object.entries(newParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      history.push({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    },
    [history, location.pathname, location.search]
  );

  // 2️⃣ Replace ALL params (REMOVES others, adds new ones)
  const setParamsOnly = useCallback(
    (newParams) => {
      const searchParams = new URLSearchParams();

      Object.entries(newParams).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          searchParams.set(key, String(value));
        }
      });

      history.push({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    },
    [history, location.pathname]
  );

  // 3️⃣ Remove specific param
  const removeParam = useCallback(
    (key) => {
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete(key);

      history.push({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    },
    [history, location.pathname, location.search]
  );

  // 4️⃣ Clear all params
  const clearAllParams = useCallback(() => {
    history.push({
      pathname: location.pathname,
      search: "",
    });
  }, [history, location.pathname]);

  // 5️⃣ Update some params AND remove others in one go
  const updateAndRemoveParams = useCallback(
    (updates, removes = []) => {
      const searchParams = new URLSearchParams(location.search);

      // Remove the keys specified in the 'removes' array
      removes.forEach((key) => searchParams.delete(key));

      // Add or update the keys in the 'updates' object
      Object.entries(updates).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "") {
          searchParams.set(key, String(value));
        } else {
          // If value is empty/null, treat it as a removal
          searchParams.delete(key);
        }
      });

      history.push({
        pathname: location.pathname,
        search: searchParams.toString(),
      });
    },
    [history, location.pathname, location.search]
  );
  return {
    params,
    addOrUpdateParams, // ✅ Add/update (keeps others)
    setParamsOnly, // ✅ Replace all (removes others)
    removeParam,
    updateAndRemoveParams, // ✅ Update some and remove others

    clearAllParams,
  };
}

export default useQueryParams;
